import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Download, Image as ImageIcon, Shuffle, FileImage } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

interface FormatInfo {
  name: string;
  extension: string;
  mimeType: string;
  description: string;
  supportsTransparency: boolean;
  supportsAnimation: boolean;
  compression: 'lossless' | 'lossy' | 'both';
}

// This will be moved inside the component to access t function

export const ImageFormatConverterTool: React.FC = () => {
  const { t } = useTranslation();

  const supportedFormats: FormatInfo[] = [
    {
      name: 'JPEG',
      extension: 'jpg',
      mimeType: 'image/jpeg',
      description: t('tools.imageFormatConverter.formats.jpeg.description'),
      supportsTransparency: false,
      supportsAnimation: false,
      compression: 'lossy'
    },
    {
      name: 'PNG',
      extension: 'png',
      mimeType: 'image/png',
      description: t('tools.imageFormatConverter.formats.png.description'),
      supportsTransparency: true,
      supportsAnimation: false,
      compression: 'lossless'
    },
    {
      name: 'WebP',
      extension: 'webp',
      mimeType: 'image/webp',
      description: t('tools.imageFormatConverter.formats.webp.description'),
      supportsTransparency: true,
      supportsAnimation: true,
      compression: 'both'
    },
    {
      name: 'BMP',
      extension: 'bmp',
      mimeType: 'image/bmp',
      description: t('tools.imageFormatConverter.formats.bmp.description'),
      supportsTransparency: false,
      supportsAnimation: false,
      compression: 'lossless'
    }
  ];

  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [originalFormat, setOriginalFormat] = useState<string>('');
  const [targetFormat, setTargetFormat] = useState<string>('png');
  const [quality, setQuality] = useState(0.9);
  const [convertedImages, setConvertedImages] = useState<{ [key: string]: string }>({});
  const [isConverting, setIsConverting] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(t('tools.imageFormatConverter.invalidImageError'));
        return;
      }

      // Detect original format
      const format = file.type.split('/')[1];
      setOriginalFormat(format);

      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
        setConvertedImages({});
      };
      reader.readAsDataURL(file);
    }
  };

  const convertToFormat = async (format: FormatInfo) => {
    if (!originalImage || !canvasRef.current) return;

    setIsConverting(true);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;

        // Clear canvas with white background for formats that don't support transparency
        if (!format.supportsTransparency) {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        ctx.drawImage(img, 0, 0);

        // Convert to target format
        const convertedDataUrl = canvas.toDataURL(
          format.mimeType,
          format.compression === 'lossy' ? quality : undefined
        );

        setConvertedImages(prev => ({
          ...prev,
          [format.extension]: convertedDataUrl
        }));

        setIsConverting(false);
      };

      img.src = originalImage;
    } catch (error) {
      console.error('Conversion error:', error);
      toast.error(t('tools.imageFormatConverter.conversionError', { format: format.name }));
      setIsConverting(false);
    }
  };

  const convertToAllFormats = async () => {
    if (!originalImage) return;

    setIsConverting(true);
    setConvertedImages({});

    for (const format of supportedFormats) {
      await convertToFormat(format);
    }
  };

  const downloadConverted = (format: FormatInfo) => {
    const convertedImage = convertedImages[format.extension];
    if (!convertedImage) return;

    const link = document.createElement('a');
    link.download = `converted-image.${format.extension}`;
    link.href = convertedImage;
    link.click();
  };

  const downloadAll = () => {
    Object.entries(convertedImages).forEach(([extension, dataUrl]) => {
      const link = document.createElement('a');
      link.download = `converted-image.${extension}`;
      link.href = dataUrl;
      link.click();
    });
  };

  const dataURLtoBlob = (dataURL: string): Blob => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const getFileSize = (dataUrl: string): string => {
    const blob = dataURLtoBlob(dataUrl);
    const bytes = blob.size;
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shuffle className="w-5 h-5" />
            <span>{t('tools.imageFormatConverter.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('common.dragDropImage')}
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
              >
                <Upload className="w-4 h-4 mr-2" />
                {t('common.selectImage')}
              </Button>
            </div>

            {originalImage && (
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('tools.imageFormatConverter.originalFormat')}: <span className="font-medium uppercase">{originalFormat}</span>
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Conversion Settings */}
      {originalImage && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.imageFormatConverter.conversionSettings')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.imageFormatConverter.qualityForLossy')}: {Math.round(quality * 100)}%
                </label>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.1"
                  value={quality}
                  onChange={(e) => setQuality(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={convertToAllFormats}
                  disabled={isConverting}
                  variant="primary"
                >
                  {isConverting ? t('tools.imageFormatConverter.converting') : t('tools.imageFormatConverter.convertToAllFormats')}
                </Button>
                
                {Object.keys(convertedImages).length > 0 && (
                  <Button
                    onClick={downloadAll}
                    variant="outline"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('tools.imageFormatConverter.downloadAll')}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Format Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.imageFormatConverter.supportedFormats')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {supportedFormats.map((format) => (
              <div
                key={format.extension}
                className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    {format.name} (.{format.extension})
                  </h4>
                  {originalImage && (
                    <Button
                      onClick={() => convertToFormat(format)}
                      disabled={isConverting}
                      size="sm"
                      variant="outline"
                    >
                      {t('tools.imageFormatConverter.convert')}
                    </Button>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {format.description}
                </p>
                <div className="flex flex-wrap gap-1 text-xs">
                  <span className={`px-2 py-1 rounded ${
                    format.supportsTransparency 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                  }`}>
                    {format.supportsTransparency ? t('tools.imageFormatConverter.transparency') : t('tools.imageFormatConverter.noTransparency')}
                  </span>
                  <span className={`px-2 py-1 rounded ${
                    format.compression === 'lossless'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                      : format.compression === 'lossy'
                      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
                      : 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                  }`}>
                    {format.compression === 'both' ? t('tools.imageFormatConverter.losslessLossy') :
                     format.compression === 'lossless' ? t('tools.imageFormatConverter.lossless') :
                     t('tools.imageFormatConverter.lossy')}
                  </span>
                </div>
                
                {convertedImages[format.extension] && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {t('tools.imageFormatConverter.size')}: {getFileSize(convertedImages[format.extension])}
                      </span>
                      <Button
                        onClick={() => downloadConverted(format)}
                        size="sm"
                        variant="primary"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        {t('common.download')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {originalImage && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.imageFormatConverter.preview')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {t('tools.imageFormatConverter.original')} ({originalFormat.toUpperCase()})
                </h4>
                <img
                  src={originalImage}
                  alt="Original"
                  className="w-full h-auto max-h-64 object-contain rounded border"
                />
              </div>
              
              {Object.entries(convertedImages).map(([extension, dataUrl]) => (
                <div key={extension}>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {t('tools.imageFormatConverter.converted')} ({extension.toUpperCase()})
                  </h4>
                  <img
                    src={dataUrl}
                    alt={`Converted to ${extension}`}
                    className="w-full h-auto max-h-64 object-contain rounded border"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};
